import type { PracticeLevel } from '@swarasense/shared';

/**
 * The on-device practice profile: small, read on nearly every page load, so
 * it lives in localStorage (contrast `practice-history.ts`'s append-only
 * IndexedDB log). No account, no network — this + the history store are the
 * whole of Phase A's local-first personalization foundation.
 */

const PROFILE_KEY = 'swarasense.profile';

/** Ear-training's pre-Phase-A "known ragas" key (`ear-known-ragas.ts`). Its
 * list is unified into `profile.myRagas` so the ear picker and the profile
 * share one list; migrated into the profile once (see `loadProfile`) rather
 * than dropped, and the key itself is left alone afterwards (harmless if
 * stale — nothing reads it once the profile has `myRagas`). */
const LEGACY_EAR_KNOWN_RAGAS_KEY = 'swarasense.earKnownRagas';

export interface Profile {
  sa?: { pitchClass: string; octave: number };
  voiceRange?: { lowHz: number; highHz: number; lowNote: string; highNote: string };
  level?: PracticeLevel;
  goals?: string[];
  myRagas?: string[];
  updatedAt?: string;
}

/** The legacy ear-known-ragas array, if `localStorage` holds a valid
 * (non-empty) one — `undefined` otherwise (absent, broken JSON, wrong
 * shape, or already-empty), so callers can tell "nothing to migrate" apart
 * from "migrate an empty list". Guarded: never throws. */
function readLegacyKnownRagas(): string[] | undefined {
  try {
    const raw = localStorage.getItem(LEGACY_EAR_KNOWN_RAGAS_KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return undefined;
    const names = parsed.filter((n): n is string => typeof n === 'string');
    return names.length > 0 ? names : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The persisted profile, `{}` if nothing's been saved yet or storage is
 * unreadable/broken (private browsing, corrupt JSON, storage disabled) —
 * guarded so a broken localStorage never breaks the app, it just falls back
 * to "no profile yet".
 *
 * One-time migration: if this profile has never had `myRagas` set (as
 * opposed to explicitly set to `[]`), and the legacy
 * `swarasense.earKnownRagas` key holds a real list, that list is copied into
 * `profile.myRagas` and persisted immediately — so the ear picker's
 * previously-chosen ragas aren't lost when it starts reading through the
 * profile instead of its own key.
 */
export function loadProfile(): Profile {
  let profile: Profile = {};
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        profile = parsed as Profile;
      }
    }
  } catch {
    profile = {};
  }

  if (profile.myRagas === undefined) {
    const legacy = readLegacyKnownRagas();
    if (legacy !== undefined) {
      profile = { ...profile, myRagas: legacy };
      saveProfile(profile);
    }
  }

  return profile;
}

/** Persists the profile; a no-op (never throws) if storage is unavailable
 * (private browsing, storage disabled). */
export function saveProfile(p: Profile): void {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
  } catch {
    // private mode / storage disabled — nothing persists this session, fine.
  }
}

/** Load the current profile, shallow-merge `patch` over it, persist, and
 * return the result — the one mutation path every caller (Sa recommendation,
 * "My ragas" edits, the You view's profile form) should use. Stamps
 * `updatedAt` on every call. */
export function updateProfile(patch: Partial<Profile>): Profile {
  const current = loadProfile();
  const next: Profile = { ...current, ...patch, updatedAt: new Date().toISOString() };
  saveProfile(next);
  return next;
}

/** The default Sa used everywhere when the profile has none yet — the app's
 * long-standing initial shruti (`app.ts`'s `initial`). Kept here so the app,
 * the content-page Sa control, and the raga/tanpura widgets all agree on the
 * first-visit shruti. */
export const DEFAULT_SA = { pitchClass: 'G', octave: 3 } as const;

/** Connected-Swarasense (W1): the ONE shared shruti. `profile.sa` if set, else
 * `DEFAULT_SA`. Read by the app store on load and by every content-page Sa
 * control, so a Sa chosen anywhere is the Sa everywhere. */
export function loadSa(): { pitchClass: string; octave: number } {
  const sa = loadProfile().sa;
  return sa ? { pitchClass: sa.pitchClass, octave: sa.octave } : { ...DEFAULT_SA };
}

/** Persist the shared shruti (thin wrapper over `updateProfile({sa})`). */
export function saveSa(pitchClass: string, octave: number): void {
  updateProfile({ sa: { pitchClass, octave } });
}

/** Subscribe to shruti changes made in ANOTHER tab or page (a `storage` event
 * only fires cross-document, never for same-document writes) — so an open
 * practice tab reflects a Sa change made on a raga/tanpura page and vice
 * versa. Returns an unsubscribe. No-op (returns a noop) where `window`/
 * `addEventListener` is unavailable. Guarded: a broken payload is ignored. */
export function subscribeSa(cb: (sa: { pitchClass: string; octave: number }) => void): () => void {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return () => {};
  }
  const handler = (e: StorageEvent): void => {
    if (e.key !== null && e.key !== PROFILE_KEY) return; // other keys (or a full clear: key===null)
    try {
      const sa = loadProfile().sa;
      if (sa) cb({ pitchClass: sa.pitchClass, octave: sa.octave });
    } catch {
      /* ignore a malformed cross-tab write */
    }
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
